import { useEffect, useState } from 'react';

import useAccountQuery from 'api/queries/useAccountQuery';
import usePlanQuery from 'api/queries/usePlanQuery';
import useSubscriptionQuery from 'api/queries/useSubscriptionQuery';
import PausedBanner from 'features/account/home/PausedBanner';
import CancelationModule from 'features/account/my-plan/CancelationModule';
import FlexPlanInformation from 'features/account/my-plan/FlexPlanInformation';
import PlanInformation from 'features/account/my-plan/PlanInformation';
import PlanOptions from 'features/account/my-plan/PlanOptions';
import PlanSummary from 'features/account/my-plan/PlanSummary';
import UpgradeModal from 'features/account/my-plan/UpgradeModal';
import UpgradePlan from 'features/account/my-plan/UpgradePlan';
import UpgradePlanBanner from 'features/account/my-plan/UpgradePlanBanner';
import NeedHelp from 'features/account/NeedHelp';
import DeliverySettings from 'features/account/upcoming-deliveries/DeliverySettings';
import { PlanModule as PlusMembershipPlanModule } from 'features/paid-membership';
import { Alert } from 'honeydew/Alert';
import Loader from 'honeydew/Loader';
import { useAccountPlusMembershipHook } from 'hooks/useAccountPlusMembershipHook';
import { trackEvent } from 'utils/analyticsUtils';
import { openModal } from 'utils/globalUtils';

function MyPlan() {
    const { data: account } = useAccountQuery();
    const { data: subscription } = useSubscriptionQuery();

    const autoShip = subscription?.variant === 'autoship';
    const canceled = subscription?.status === 'CANCELLED';
    const paused = subscription?.status === 'PAUSED';

    const planQueryEnabled = !autoShip || canceled;

    const { data: plan } = usePlanQuery({
        enabled: planQueryEnabled,
        subscriptionID: subscription?.id,
    });

    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [upgradeModal, setUpgradeModal] = useState(false);

    const { inPlusMembershipTreatment } = useAccountPlusMembershipHook();

    const openUpgradeModal = () => {
        openModal(setUpgradeModal);
        trackEvent('upgrade plan flow start');
    };

    useEffect(() => {
        setError(canceled ? 'Your plan is currently canceled.' : '');
    }, [canceled]);

    if (!account || !subscription || (planQueryEnabled && !plan)) {
        return (
            <div className="flex h-[300px] items-center justify-center">
                <Loader width="60px" />
            </div>
        );
    }

    const getAlertCopy = () => {
        if (success === 'downgrade') {
            return (
                <>
                    <span className="font-grotesk-bold">Your subscription has been canceled</span> You&apos;ll no longer
                    get automatic orders, but you can still shop with your Flex Plan. Learn more below!
                </>
            );
        }
        if (success === 'paused') {
            return (
                <>
                    <span className="block font-grotesk-bold">Your plan has been paused.</span> Unskip charges at any
                    time to resume.
                </>
            );
        }
        return (
            <>
                <span className="block font-grotesk-bold">
                    Welcome back! Your Grocery Subscription Plan is now up and running.
                </span>
                <span>Learn more about your plan and specify any food preferences below.</span>
            </>
        );
    };

    return (
        <section>
            {error !== '' && (
                <div className="mb-20">
                    <Alert variant="error">{error}</Alert>
                </div>
            )}
            {success !== '' && !canceled && (
                <div className="mb-20">
                    <Alert variant="success" onClose={() => setSuccess('')}>
                        {getAlertCopy()}
                    </Alert>
                </div>
            )}

            {canceled ? (
                <UpgradePlan account={account} plan={plan} subscription={subscription} setSuccess={setSuccess} />
            ) : (
                <>
                    <h1 className="text-heading-sm my-20 mt-0">My Plan</h1>
                    {paused && <PausedBanner account={account} />}
                    {!autoShip ? (
                        <>
                            <FlexPlanInformation account={account} success={success} />
                            <UpgradePlanBanner openUpgradeModal={openUpgradeModal} />
                        </>
                    ) : (
                        <PlanInformation />
                    )}
                    {inPlusMembershipTreatment && <PlusMembershipPlanModule isFlexPlan={!autoShip} paused={paused} />}
                    <PlanSummary account={account} autoShip={autoShip} />
                    <DeliverySettings account={account} subscription={subscription} setError={setError} />
                    {!autoShip && <PlanOptions openUpgradeModal={openUpgradeModal} />}
                    <CancelationModule
                        account={account}
                        autoShip={autoShip}
                        openUpgradeModal={openUpgradeModal}
                        subscription={subscription}
                        setSuccess={setSuccess}
                    />

                    {!window.isNativeApp && <NeedHelp />}
                </>
            )}

            {upgradeModal && (
                <UpgradeModal
                    account={account}
                    plan={plan}
                    subscription={subscription}
                    upgradeModal={upgradeModal}
                    setUpgradeModal={setUpgradeModal}
                    setSuccess={setSuccess}
                />
            )}
        </section>
    );
}

export { MyPlan as Component };
